/**
 * Programmatic audio feedback on quiz answers. No asset files — sounds are
 * generated via Web Audio API (sub-millisecond, no bundle cost).
 *
 * iOS Safari quirks worth knowing:
 * - The AudioContext starts in "suspended" state and must be resumed inside a
 *   user gesture (touchend / click handler). After it's resumed once it stays
 *   running for the rest of the session.
 * - The chain from gesture to playback must NOT cross an `await` — once a
 *   promise resolves later, iOS considers the gesture context gone. The store
 *   calls these helpers synchronously before any await for that reason.
 * - The physical mute switch on iPhone silences Web Audio output. There is no
 *   reliable workaround in the browser; document it in the UI. A native
 *   wrapper (Capacitor) can opt the app into the "playback" audio session
 *   category to play through the mute switch — see the v0.4 → v0.5 roadmap.
 */

let audioCtx: AudioContext | null = null;

function ensureCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!audioCtx || audioCtx.state === "closed") {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    audioCtx = new Ctor();
  }
  // resume() must be invoked synchronously inside the user gesture on iOS.
  // Fire-and-forget the returned promise — the actual oscillator calls below
  // will queue on top of the resumed context.
  if (audioCtx.state === "suspended") {
    audioCtx.resume().catch(() => {
      /* ignore — we'll retry on the next call */
    });
  }
  return audioCtx;
}

/**
 * Eagerly create + resume the AudioContext in a user gesture handler so later
 * sound playback (even after async work) is allowed. Safe to call multiple
 * times.
 */
export function unlockAudio(): void {
  ensureCtx();
}

function tone(freq: number, duration: number, type: OscillatorType = "sine", delay = 0): void {
  const ctx = ensureCtx();
  if (!ctx) return;
  const startAt = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startAt);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(0.18, startAt + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
  osc.connect(gain).connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.05);
}

export function playCorrect(): void {
  // Rising two-note chime (E5 → G#5) — feels like a positive confirmation.
  tone(659.25, 0.12, "sine", 0);
  tone(830.61, 0.16, "sine", 0.08);
}

export function playWrong(): void {
  // Falling sawtooth — clearly a negative cue without being startling.
  tone(220, 0.18, "sawtooth", 0);
  tone(146.83, 0.24, "sawtooth", 0.1);
}

